<script setup lang="ts" generic="T extends string | number">
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import type { ContentSliderItem } from '~/components/ContentSlider.client.vue'

export interface PhotoModalOption {
  label: string
  key: string
  fontAwesomeIcon: string
}

const props = defineProps<{
  modelValue?: T
  photos: {
    id: T
    url: string
  }[]
  navigationDisabled?: boolean
  options?: PhotoModalOption[]
}>()

const emit = defineEmits<{
  (e: 'update:model-value', value: T): void
  (e: 'close'): void
  (e: 'option-select', key: string): void
}>()

const mainStore = useMainStore()

const currentId = ref<T | undefined>(props.modelValue ?? props.photos[0]?.id)

const sliderModelValue = computed<ContentSliderItem<T> | undefined>(() =>
  currentId.value !== undefined ? { identifier: currentId.value } : undefined
)

const sliderItems = computed<ContentSliderItem<T>[]>(() =>
  props.photos.map((p) => ({ identifier: p.id }))
)

const onSliderUpdate = (item: ContentSliderItem<T>) => {
  if (props.navigationDisabled) return

  currentId.value = item.identifier
  emit('update:model-value', item.identifier)
}

const close = () => {
  emit('close')
}

const getPhotoByIdentifier = (id: T) => {
  return props.photos.find((p) => p.id === id)
}

const onOptionSelect = (key: string, closeDropdown: () => void) => {
  closeDropdown()
  emit('option-select', key)
}

const goToOffset = (offset: number) => {
  if (props.navigationDisabled) return
  if (currentId.value === undefined) return

  const currentIndex = props.photos.findIndex((p) => p.id === currentId.value)
  if (currentIndex === -1) return

  const nextPhoto = props.photos[currentIndex + offset]
  if (!nextPhoto) return

  currentId.value = nextPhoto.id
  emit('update:model-value', nextPhoto.id)
}

const handleKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Escape') {
    close()
  } else if (event.key === 'ArrowLeft') {
    goToOffset(-1)
  } else if (event.key === 'ArrowRight') {
    goToOffset(1)
  }
}

watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue !== undefined) currentId.value = newValue
  }
)

onMounted(() => {
  mainStore.hideScrollbar()
  window.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  mainStore.unhideScrollbar()
  window.removeEventListener('keydown', handleKeydown)
})
</script>

<template>
  <div
    class="tw-fixed tw-left-0 tw-top-0 tw-flex tw-h-screen tw-w-screen tw-flex-col tw-bg-black/90 tw-backdrop-blur"
    style="z-index: 110"
  >
    <div class="tw-flex tw-w-full tw-items-center tw-gap-6 tw-bg-black/10 tw-px-6 tw-py-4">
      <FontAwesomeIcon
        icon="xmark"
        class="tw-cursor-pointer tw-text-2xl tw-text-white/80"
        @click="close"
      />

      <slot name="user-info" />

      <Dropdown v-if="props.options && props.options.length > 0" class="tw-z-10 tw-ml-auto">
        <template #header>
          <InputAppButton class="tw-cursor-pointer tw-p-2 tw-text-white/80">
            <FontAwesomeIcon icon="ellipsis-v" class="tw-text-xl" />
          </InputAppButton>
        </template>

        <template #content="{ close: closeDropdown }">
          <div
            class="tw-absolute tw-right-0 tw-top-full tw-mt-2 tw-flex tw-flex-col tw-whitespace-nowrap tw-rounded-lg tw-bg-gray-800/80 tw-text-white tw-shadow-lg tw-backdrop-blur-sm"
          >
            <InputAppButton
              v-for="option in props.options"
              :key="option.key"
              class="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-rounded tw-px-4 tw-py-2 tw-text-sm hover:tw-bg-white/10"
              @click="onOptionSelect(option.key, closeDropdown)"
            >
              <FontAwesomeIcon :icon="option.fontAwesomeIcon" class="tw-w-3 tw-text-white/60" />
              {{ option.label }}
            </InputAppButton>
          </div>
        </template>
      </Dropdown>
    </div>

    <div class="tw-relative tw-min-h-0 tw-flex-grow">
      <ContentSlider
        :model-value="sliderModelValue"
        :items="sliderItems"
        :hide-navigation="props.navigationDisabled"
        class="tw-h-full"
        :slides-list-classes="
          props.navigationDisabled
            ? 'tw-pb-3 tw-pt-2 tw-overflow-hidden tw-pointer-events-none'
            : 'tw-pb-3 tw-pt-2'
        "
        @update:model-value="onSliderUpdate"
      >
        <template #item="{ identifier }">
          <div
            class="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center"
            @click.self="close"
          >
            <div class="tw-relative tw-inline-flex tw-max-h-full tw-min-h-0 tw-max-w-full">
              <img
                :src="getPhotoByIdentifier(identifier)?.url"
                class="tw-max-h-full tw-max-w-full tw-object-contain"
              />

              <div class="tw-absolute tw-inset-x-0 tw-bottom-4 tw-flex tw-justify-center tw-px-4">
                <slot name="item-action" :identifier="identifier" />
              </div>
            </div>
          </div>
        </template>

        <template #arrow="{ direction }">
          <div
            class="tw-cursor-pointer tw-rounded tw-bg-white/10 tw-px-2 tw-py-3 tw-text-2xl tw-text-white tw-transition-all tw-duration-200"
          >
            <font-awesome-icon :icon="direction === 'left' ? 'chevron-left' : 'chevron-right'" />
          </div>
        </template>

        <template #indicator="{ currentIndex, totalItems }">
          <div class="tw-w-full">
            <div class="tw-mb-4 tw-text-center">
              <span class="tw-text-sm tw-text-white/60">
                {{ currentIndex + 1 }} / {{ totalItems }}
              </span>
            </div>

            <slot name="bottom-bar" />
          </div>
        </template>
      </ContentSlider>

      <slot name="content-extra" />
    </div>
  </div>
</template>
